/**
 * CURRENT PHASE HOOK
 * 
 * Centralized React hook for fetching and caching the current active investment phase.
 * Provides automatic refetching, error handling, and Phase 2 fallback as per project requirements.
 * 
 * CRITICAL: Default fallback is ALWAYS Phase 2 ($10/share), never Phase 1.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { InvestmentPhase } from '../lib/services/phaseSpecificService';

export interface UseCurrentPhaseReturn {
  currentPhase: InvestmentPhase | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to get the current active investment phase with caching
 * @param autoRefresh - Whether to automatically refetch phase data periodically (default: false)
 * @param refreshInterval - Interval in milliseconds for auto-refresh (default: 60000 = 1 minute)
 */
export const useCurrentPhase = (
  autoRefresh: boolean = false,
  refreshInterval: number = 60000
): UseCurrentPhaseReturn => {
  const [currentPhase, setCurrentPhase] = useState<InvestmentPhase | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch the current active phase from database
   * Falls back to Phase 2 if no active phase is found
   */
  const fetchCurrentPhase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Query for active phase
      const { data, error: queryError } = await supabase
        .from('investment_phases')
        .select('*')
        .eq('is_active', true)
        .order('phase_number', { ascending: true })
        .limit(1);

      if (queryError) {
        console.error('Error fetching active phase:', queryError);
        throw new Error('Failed to fetch active phase');
      }

      // If we have an active phase, use it
      if (data && data.length > 0) {
        setCurrentPhase(data[0]);
        console.log('✅ Active phase loaded:', data[0].phase_name);
        return;
      }

      // No active phase found - fall back to Phase 2 ($10/share) as per project requirements
      console.warn('⚠️ No active phase found, falling back to Phase 2');
      const { data: phase2Data, error: phase2Error } = await supabase
        .from('investment_phases')
        .select('*')
        .eq('phase_number', 2)
        .single();

      if (phase2Error || !phase2Data) {
        console.error('Error fetching Phase 2 fallback:', phase2Error);
        throw new Error('No active phase and Phase 2 fallback not available');
      }

      setCurrentPhase(phase2Data);
      console.log('✅ Using Phase 2 fallback:', phase2Data.phase_name);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error in fetchCurrentPhase:', err);
      setCurrentPhase(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchCurrentPhase();
  }, [fetchCurrentPhase]);

  // Auto-refresh if enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const intervalId = setInterval(() => {
      console.log('🔄 Auto-refreshing current phase...');
      fetchCurrentPhase();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, fetchCurrentPhase]);

  return {
    currentPhase,
    loading,
    error,
    refetch: fetchCurrentPhase
  };
};

